import { useContext, useEffect, useRef, useState } from 'react';
import { DateTime } from 'luxon';
import { useSearchParams } from 'react-router-dom';

import styles from './Home.module.css';

import { NAVBAR_ICON_URL } from '../../../constants';
import { Modal } from '../../../components/modal/Modal';
import { AboutButton } from '../../../components/about/About';
import { Icon } from '../../../components/icon/Icon';
import { EyeIndicator } from '../../../views/drafts/Drafts';
import { InstallationDialog } from '../../../views/home/Home';

import { InstallBtn } from '../../../views/installation/Installation';

import { Context, Mode } from './Home.context';
import { Magazines } from './sections/magazines/Magazines';
import { Collections } from './sections/collections/Collections';
import { AppContext } from '../../../App.context';

export const HomeView = () => {
	const [searchParams, setSearchParams] = useSearchParams();
	const [showSubscribe, setShowSubscribe] = useState(false);
	const [showInstallation, setShowInstallation] = useState(false);
	const [mode, setMode] = useState<Mode>(
		searchParams.get('section') === 'collections'
			? 'collections'
			: 'issues',
	);

	useEffect(() => {
		const key = 'subscription-dialog-exp';

		const validateExp = () => {
			const now = DateTime.now();
			if (!localStorage.getItem(key)) {
				const expirationDt = now.plus({ minutes: 10 });
				localStorage.setItem(key, expirationDt.toISO());
				setShowSubscribe(true);
				return;
			}

			const expirationDt = DateTime.fromJSDate(
				new Date(`${localStorage.getItem(key)}`),
			);

			if (now >= expirationDt) {
				localStorage.removeItem(key);
				validateExp();
			}
		};

		validateExp();
	}, []);

	useEffect(() => {
		setSearchParams((params) => {
			params.set('section', mode);
			return params;
		});
	}, [mode]);
	return (
		<Context.Provider
			value={{
				mode,
				setMode,
				setShowSubscribe,
				showSubscribe,
				setShowInstallation,
			}}
		>
			<SubscriptionDialog />
			<InstallationDialog
				setShowInstallation={setShowInstallation}
				show={showInstallation}
			/>
			<div className={styles['home']}>
				<div className={styles['view']}>
					<Topbar />
					<Content />
					<Navbar />
				</div>
			</div>
		</Context.Provider>
	);
};

function Topbar() {
	const { setShowInstallation } = useContext(Context);
	const { isInstalled } = useContext(AppContext);
	return (
		<div className={styles['topbar']}>
			<div>
				<AboutButton />
				<EyeIndicator />
			</div>
			<div className={styles['title']}>
				<img src={NAVBAR_ICON_URL} alt="Logo" />
			</div>
			<div>
				<ButtonBar />
				{!isInstalled ? (
					<InstallBtn
						onClick={() => {
							setShowInstallation(true);
						}}
					/>
				) : null}
			</div>
		</div>
	);
}

function Content() {
	const { mode } = useContext(Context);
	return (
		<div className={styles['content']}>
			{mode === 'collections' ? <Collections /> : <Magazines />}
		</div>
	);
}

function Navbar() {
	return (
		<nav>
			<ButtonBar />
		</nav>
	);
}

function ButtonBar() {
	const ref = useRef<HTMLDivElement>(null);
	const { mode, setMode } = useContext(Context);

	useEffect(() => {
		const root = document.documentElement;
		if (!root) {
			return;
		}
		if (!ref) {
			return;
		}
		if (!ref.current) {
			return;
		}

		const current = ref.current;
		if (current.children.length) {
			root.style.setProperty(
				'--bottom-bar-height',
				'calc(var(--navbar-height) + var(--safe-area-inset))',
			);
			return;
		}

		root.style.setProperty('--bottom-bar-height', '0px');
	}, [ref]);

	return (
		<div ref={ref} className={styles['button-bar']}>
			<button
				type={'button'}
				key={'collections'}
				title="Collections"
				data-text="Collections"
				onClick={() => {
					setMode('collections');
				}}
				className={[
					mode === 'collections' ? styles['active'] : '',
					'tooltip',
					'down',
				].join(' ')}
			>
				<Icon color="#000" name="globe" />
				Collections
			</button>
			<button
				type={'button'}
				key={'issues'}
				title="Issues"
				data-text="Issues"
				onClick={() => {
					setMode('issues');
				}}
				className={[
					mode === 'issues' ? styles['active'] : '',
					'tooltip',
					'down',
				].join(' ')}
			>
				<Icon color="#000" name="library" />
				Issues
			</button>
		</div>
	);
}

function SubscriptionDialog() {
	const { showSubscribe, setShowSubscribe } = useContext(Context);
	return (
		<Modal
			hasCloseBtn={true}
			classNames={[styles['subscription-dialog-container']]}
			isOpen={showSubscribe}
			onClose={() => {
				setShowSubscribe(false);
			}}
		>
			<div className={styles['subscription-dialog']}>
				<h3>
					Independent investment research, curated for financial
					professionals
				</h3>
				<p>
					Uncover new investing insights with a subscription to
					Morningstar magazine. Four times a year, you&apos;ll get the
					best of our analysis, commentary, and original research
					delivered to you for free.
				</p>
				<a
					href="https://www.morningstar.com/business/insights/magazine"
					target={'_blank'}
					rel="noreferrer"
				>
					Subscribe now -&gt;
				</a>
			</div>
		</Modal>
	);
}
